import type { Entities, FirestoreEntity } from './entities';
import type { CommentCount } from './comment';

export function formatFileName(fileName: string) {
  // Replace '-' and '_' with a space
  let formattedName = fileName.replace(/[-_]/g, ' ');

  // Remove file ending (assumes the file ending starts with a dot)
  const dotIndex = formattedName.lastIndexOf('.');
  if (dotIndex !== -1) {
    formattedName = formattedName.substring(0, dotIndex);
  }

  return formattedName;
}

export type DocumentType = 'revenue' | 'expense' | 'other';

export type FieldName =
  | 'type'
  | 'due_date'
  | 'net_amount'
  | 'receiver_name'
  | 'supplier_name'
  | 'supplier_short_name'
  | 'supplier_payment_ref'
  | 'total_amount'
  | 'document_date'
  | 'invoice_id'
  | 'category';

export const fieldNames = [
  'type',
  'due_date',
  'net_amount',
  'receiver_name',
  'supplier_name',
  'supplier_short_name',
  'supplier_payment_ref',
  'total_amount',
  'document_date',
  'invoice_id',
  'category',
];

export type DocumentAiState = 'processing' | 'ready' | 'error' | 'no result' | 'reprocess';

export type CategoryColors =
  | 'grey'
  | 'red'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'pink';

export const allCategoryColors: CategoryColors[] = [
  'grey',
  'red',
  'yellow',
  'green',
  'blue',
  'indigo',
  'purple',
  'pink',
];

export interface Category {
  id: string;
  displayName: string;
  color: CategoryColors;
}

export interface MailMessage {
  fromEmail: string;
  toEmail: string;
  subject: string;
  text: string;
  documentCount: number;
}

export interface CommonDocument extends CommentCount {
  type: DocumentType;
  name: string;
  filePath: string;
  fileName: string;
  orgaId: string;

  comment: string | null;
  documentDate: string | null;

  folder: EntityReference | null;

  accounting: {
    relevant: boolean;
    processed: boolean;
  };

  mail?: MailMessage;

  linkedEntities?: Record<string, EntityReference>;
  linkedUsers?: Record<string, UserReference>;

  unreadBy: string[];

  curatedValuesReviewed?: boolean;
  documentSearchTokens?: string[] | null;
  documentText?: string | null;
  documentSummary?: string | null;
  documentAiState?: DocumentAiState | null;
  documentAI?: {
    curated?: Record<FieldName, string>;
    raw?: any;
  };
}

interface InvoiceDocument extends CommonDocument {
  amountInCents: number;
  valueDate: string; // Valuta
  financialYear: number;
  paid: boolean;
  category?: Category;
}
export interface RevenueDocument extends InvoiceDocument {
  type: 'revenue';
  internalInvoice: boolean;
}

export interface ExpenseDocument extends InvoiceDocument {
  type: 'expense';
}

export interface OtherDocument extends CommonDocument {
  type: 'other';
}

export interface SearchIndexDocument extends CommonDocument {
  category?: Category;
  internalInvoice?: boolean;
  path: string;
  objectID: string;
}

export type Document = RevenueDocument | ExpenseDocument | OtherDocument;

export type FirestoreDocument = FirestoreEntity<Document>;

export interface UserReference {
  userId: string;
  displayName: string;
  accepted: boolean;
}

export interface EntityReference {
  entity: Entities;
  id: string;
  name: string;
}

export interface ArchiveFolder {
  id: string;
  active: boolean;
  name: string;
  documentCount: number;
  sharedDocumentCount: number;
}

export interface Archive {
  folders: Record<string, ArchiveFolder>;
  folderSort: string[];
}

export type FirestoreArchive = FirestoreEntity<Archive>;
